import React, { useEffect, useState } from "react"
import InputBox from "../../../Components/Input"
import { FiHelpCircle } from "react-icons/fi";
import SelectDropdown from "../../../Components/InputSelect";
import RadioButton from "../../../Components/RadioButton";
import Checkbox from "../../../Components/Checkbox";
import { API_ENDPOINTS } from "../../../utils/api";
import createAxiosInstance from "../../../utils/axiosInstance";
import {
  accompanyingChildren,
  ageDropDown,
  ageOfChildren,
  currentHousingSituation,
  currentMentalHealth,
  currentSubstanceUse,
  englishProficiency,
  historyOfViolence,
  languageSpoken,
  numberOfChildren,
  nycAgeDropDown,
  physicalDisability,
  traffickingSituation,
} from "../../../utils/new-request-form";
import { useForm, SubmitHandler } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { cleanObject } from "../../../utils/helper";
import Loader from "../../../Components/Loader";
import useMyAgencyData from "../../../hooks/useMyAgencyData";

const nycGroupIds = (process.env.REACT_APP_NYC_GROUP_IDS || "")
  .split(",")
  .map((id) => id.trim().toLowerCase())
  .filter(Boolean);

const rhyGroupIds = (process.env.REACT_APP_RHY_GROUP_IDS || "")
  .split(",")
  .map((id) => id.trim().toLowerCase())
  .filter(Boolean);

const NewRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [groups, setGroups] = useState<any[]>([]);
  const [isLoding, setIsLoding] = useState(false);
  const [prevValueAccompanyingChildren, setPrevValueAccompanyingChildren] = useState<string[] | null>([]);
  const [prevValueHistory, setPrevValueHistory] = useState<string[] | null>([]);
  const [prevValuePhysicalDisability, setPrevPhysicalDisability] = useState<string[] | null>([]);
  const axiosInstance = createAxiosInstance();

  interface accompanyingChildrenDetails {
    age: string;
  }

  interface Assessment {
    age: string;
    accompanyingChildren: string[];
    accompanyingChildrenCount: string;
    accompanyingChildrenDetails: accompanyingChildrenDetails[];
    history: string[];
    mentalHealth: string;
    currentSubstanceUse: string;
    primaryLanguage: string;
    secondaryLanguage: string;
    englishProficiency: string;
    traffickingSituation: string[];
    traffickingSituationSpecial: string;
    physicalDisability: string[];
    physicalDisabilitySpecial: string;
    currentHousingSituation?: string;
    languageSpoken?: string;
    languageSpokenSpecial?: string;
    additionalInformation?: string;
  }

  interface FormData {
    caseReferenceId: string;
    groupIdStr: string;
    assessments: Assessment[];
  }

  const defaultValues: FormData = {
    caseReferenceId: "",
    groupIdStr: "",
    assessments: [
      {
        age: "",
        accompanyingChildren: [],
        accompanyingChildrenCount: "",
        accompanyingChildrenDetails: [],
        history: [],
        mentalHealth: "",
        currentSubstanceUse: "",
        primaryLanguage: "",
        secondaryLanguage: "",
        englishProficiency: "",
        traffickingSituation: [],
        traffickingSituationSpecial: "",
        physicalDisability: [],
        physicalDisabilitySpecial: "",
        additionalInformation: "",
      }
    ]
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });

  const accompanyingChildrenWatch = watch("assessments.0.accompanyingChildren") || [];
  const accompanyingChildrenCountWatch = watch("assessments.0.accompanyingChildrenCount") || "";
  const historyWatch = watch("assessments.0.history") || [];
  const physicalDisabilityWatch = watch("assessments.0.physicalDisability") || [];
  const additionalInformationWatch = watch("assessments.0.additionalInformation") || "";

  //  radio button value checked
  const [selectedValue, setSelectedValue] = useState("option1");
  const agency = useMyAgencyData();

  useEffect(() => {
    getGroups();
  }, [agency]);

  const getGroups = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.BEDFINDER.GETGROUPS);

      const allowedGroupIds =
        agency?.groupInfo
          ?.filter((g: any) => g.canSendRequest)
          ?.map((g: any) => g.groupId) || [];

      const filteredGroups = response.data
        .filter((g: any) => allowedGroupIds.includes(g.groupId))
        .map((g: any) => ({
          label: g.name,
          value: g.groupId,
        }));
      setGroups(filteredGroups);
      setValue("groupIdStr", filteredGroups[0]?.value || "");
    } catch (error) {
      console.log(error);
    }
  };


  const selectedGroupId = watch("groupIdStr");
  const isNYC = !!selectedGroupId && nycGroupIds.includes(selectedGroupId);
  const isRHY = !!selectedGroupId && rhyGroupIds.includes(selectedGroupId);
  const isSurvivorPopulation = agency?.survivorPopulation && agency.survivorPopulation.length > 0;

  let mode = "legacy";
  if (isRHY) {
    mode = "rhy";
  } else if (isNYC) {
    mode = "nyc";
  } else if (isSurvivorPopulation) {
    mode = "survivor";
  }

  const ageOptions = mode === "nyc" ? nycAgeDropDown : ageDropDown;

  useEffect(() => {
    if (accompanyingChildrenWatch.length > 0) {
      let currentValues = [...accompanyingChildrenWatch];

      if (JSON.stringify(prevValueAccompanyingChildren) === JSON.stringify(currentValues)) {
      } else if (!prevValueAccompanyingChildren?.includes(accompanyingChildren[0].value) && currentValues.includes(accompanyingChildren[0].value)) {
        currentValues = [accompanyingChildren[0].value];
        setValue("assessments.0.accompanyingChildren", currentValues);
        setPrevValueAccompanyingChildren(currentValues);
      } else if (prevValueAccompanyingChildren?.includes(accompanyingChildren[0].value) && currentValues.includes(accompanyingChildren[0].value)) {
        currentValues = currentValues.filter((value) => value !== accompanyingChildren[0].value);
        setValue("assessments.0.accompanyingChildren", currentValues);
        setPrevValueAccompanyingChildren(currentValues);
      } else {
        setPrevValueAccompanyingChildren(currentValues);
      }
    }
  }, [accompanyingChildrenWatch, setValue]);

  useEffect(() => {
    if (historyWatch.length > 0) {
      let currentValues = [...historyWatch];

      if (JSON.stringify(prevValueHistory) === JSON.stringify(currentValues)) {
      } else if (!prevValueHistory?.includes(historyOfViolence[0].value) && currentValues.includes(historyOfViolence[0].value)) {
        currentValues = [historyOfViolence[0].value];
        setValue("assessments.0.history", currentValues);
        setPrevValueHistory(currentValues);
      } else if (prevValueHistory?.includes(historyOfViolence[0].value) && currentValues.includes(historyOfViolence[0].value)) {
        currentValues = currentValues.filter((value) => value !== historyOfViolence[0].value);
        setValue("assessments.0.history", currentValues);
        setPrevValueHistory(currentValues);
      } else {
        setPrevValueHistory(currentValues);
      }
    }
  }, [historyWatch, setValue]);

  useEffect(() => {
    if (physicalDisabilityWatch.length > 0) {
      let currentValues = [...physicalDisabilityWatch];

      if (JSON.stringify(prevValuePhysicalDisability) === JSON.stringify(currentValues)) {
      } else if (!prevValuePhysicalDisability?.includes(physicalDisability[0].value) && currentValues.includes(physicalDisability[0].value)) {
        currentValues = [physicalDisability[0].value];
        setValue("assessments.0.physicalDisability", currentValues);
        setPrevPhysicalDisability(currentValues);
      } else if (prevValuePhysicalDisability?.includes(physicalDisability[0].value) && currentValues.includes(physicalDisability[0].value)) {
        currentValues = currentValues.filter((value) => value !== physicalDisability[0].value);
        setValue("assessments.0.physicalDisability", currentValues);
        setPrevPhysicalDisability(currentValues);
      } else {
        setPrevPhysicalDisability(currentValues);
      }
    }
  }, [physicalDisabilityWatch, setValue]);

  useEffect(() => {
    if (accompanyingChildrenCountWatch !== undefined && accompanyingChildrenCountWatch !== "") {
      const count = parseInt(accompanyingChildrenCountWatch, 10) || 0;

      setValue(
        "assessments.0.accompanyingChildrenDetails",
        Array.from({ length: count }, (_, index) => ({
          age: ""
        }))
      );
    }
  }, [accompanyingChildrenCountWatch, setValue]);

  const submitHandler: SubmitHandler<FormData> = async (formData) => {
    try {
      setIsLoding(true);
      const data = cleanObject(formData);
      data.groupId = [];
      data.groupId.push(data.groupIdStr);
      data.assessments[0].accompanyingChildrenCount = data.assessments[0].accompanyingChildrenCount === "7" ? "More than 6" : data.assessments[0].accompanyingChildrenCount;
      delete data.groupIdStr;
      // Remove gender fields from all forms
      delete data.assessments[0].genderId;
      delete data.assessments[0].genderIdSpecial;
      
      // Remove gender from children details
      if (data.assessments[0].accompanyingChildrenDetails) {
        data.assessments[0].accompanyingChildrenDetails = data.assessments[0].accompanyingChildrenDetails.map((child: any) => {
          const { gender, other, ...rest } = child;
          return rest;
        });
      }
      
      // Doing double check - Removing fields if isNYC is true
      if (mode === "nyc") {
        delete data.assessments[0].history;
        delete data.assessments[0].mentalHealth;
        delete data.assessments[0].currentSubstanceUse;
        delete data.assessments[0].traffickingSituation;
        delete data.assessments[0].additionalInformation;
      } 
      // Doing double check - Removing fields if isRHY is true
       else if (mode === "rhy") {
        delete data.assessments[0].history;
        delete data.assessments[0].mentalHealth;
        delete data.assessments[0].currentSubstanceUse;
        delete data.assessments[0].traffickingSituation;
      } else { 
        delete data.assessments[0].languageSpoken;
        delete data.assessments[0].languageSpokenSpecial;
        delete data.assessments[0].currentHousingSituation;
      }

      const needCreate = await axiosInstance.post(
        API_ENDPOINTS.BEDFINDER.CREATENEED,
        data
      );

      if (needCreate.status === 200) {
        const requestCreate = await axiosInstance.post(
          API_ENDPOINTS.BEDFINDER.CREATEREQUEST.replace("{needId}", needCreate.data.needId)
        );

        if (requestCreate.status === 200) {
          setIsLoding(false);
          navigate("/bedfinder");
        }
      }
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  }

  return (
    <div className="container py-4 md:py-10 2xl:py-[70px]">
      {isLoding && <Loader />}
      <div className="mb-4">
        <h3 className="text-gray-900 font-semibold text-base lg:text-lg 2xl:text-xl mb-2">Create a New Bed Request</h3>
      </div>
      <form
        name="closeForm"
        onSubmit={handleSubmit(submitHandler)}
        noValidate
      >
        <div className="bg-gray-100 rounded-xl flex flex-wrap sm:flex-nowrap items-center sm:gap-3 lg:gap-0 p-4 md:p-6 lg:space-x-10 lg:py-8 lg:px-10">
          <div className="w-full sm:w-1/2">
            <InputBox
              type="text"
              placeholder=""
              label={`CASE REFERENCE #`}
              labelIcon={<FiHelpCircle size={18} />}
              tooltipText="This is for your internal tracking purposes only, and will not be shared with other agencies."
              {...register("caseReferenceId", { required: true })}
            />
            {errors.caseReferenceId?.type === "required" && (
              <label className="text-error">
                This field is required.
              </label>
            )}
          </div>
          <div className="w-full sm:w-1/2 mt-3 sm:mt-0">
            <SelectDropdown
              options={groups}
              name="groupIdStr"
              placeholder="Choose an option"
              label={`Selected groups`}
              value={watch("groupIdStr")}
              register={register}
              setValue={setValue}
            />
            {errors.groupIdStr?.type === "required" && (
              <label className="text-error">
                This field is required.
              </label>
            )}
          </div>
        </div>
        {selectedGroupId && (
          <ol className="list-decimal ml-5 mt-6">
            {/* Age */}
            <li className="mb-8">
              <div className="text-gray-900 font-medium text-base"> Age</div>
              <div className="flex flex-wrap gap-x-4 lg:gap-0 lg:space-x-6">
                {ageOptions.map((item: string, index: number) => (
                  <div className="mt-3" key={index}>
                    <RadioButton
                      label={item} value={item}
                      selectedValue={selectedValue}
                      radioTextClass="2xl:text-base"
                      radioClass="lg:mb-3 sm:text-gray-600"
                      {...register("assessments.0.age", { required: true })}
                    />
                  </div>
                ))}
              </div>
              {errors?.assessments?.[0]?.age?.type === "required" && (
                <label className="text-error">
                  This field is required.
                </label>
              )}
            </li>
            {/* Current housing situation */}
             {mode === "rhy" && (
               <li className="mb-8">
                 <div className="text-gray-900 font-medium text-base">
                   Current housing situation
                 </div>
                 <div className="flex flex-wrap gap-x-5">
                   {currentHousingSituation.map((item: any, index: number) => (
                     <div className="mt-3" key={index}>
                       <RadioButton
                         label={item.label}
                         value={item.value}
                         selectedValue={selectedValue}
                         radioTextClass="2xl:text-base"
                         radioClass="lg:mb-3 sm:text-gray-600"
                         {...register(`assessments.0.currentHousingSituation`, {
                           required: true,
                         })}
                       />
                     </div>
                   ))}
                 </div>
                 {errors?.assessments?.[0]?.currentHousingSituation?.type ===
                   "required" && (
                   <label className="text-error">This field is required.</label>
                 )}
               </li>
             )}
            {/* childern */}
            <li className="mb-8">
              <div className="text-gray-900 font-medium text-base"> Accompanying children/derivatives</div>
              <div className="flex flex-wrap gap-x-6 lg:gap-0">
                {accompanyingChildren.map((item: any, index: number) => (
                  <div className="lg:w-1/3 mt-3" key={index}>
                    <Checkbox
                      key={index}
                      label={item.label}
                      checkboxTextClass="2xl:text-base"
                      value={item.value}
                      selectedValue={watch("assessments.0.accompanyingChildren")}
                      {...register("assessments.0.accompanyingChildren", { required: true })}
                    />
                  </div>
                ))}
                {errors?.assessments?.[0]?.accompanyingChildren?.type === "required" && (
                  <label className="text-error">
                    This field is required.
                  </label>
                )}
              </div>
              {accompanyingChildrenWatch.includes(accompanyingChildren[accompanyingChildren.length - 1].value) && (
                <div className="bg-gray-100 rounded-xl py-8 px-10 mt-5">
                  <div className="w-1/3 flex gap-x-6 items-center">
                    <label htmlFor="childern" className="text-md mb-2 font-medium text-gray-700 w-60">Number of children:</label>
                    <div className="w-full">
                      <SelectDropdown
                        options={numberOfChildren}
                        name="assessments.0.accompanyingChildrenCount"
                        placeholder="Choose an option"
                        value={watch("assessments.0.accompanyingChildrenCount")}
                        register={register}
                        setValue={setValue}
                      />
                      {errors?.assessments?.[0]?.accompanyingChildrenCount?.type === "required" && (
                        <label className="text-error">
                          This field is required.
                        </label>
                      )}
                    </div>
                  </div>
                  {!isNaN(parseInt(accompanyingChildrenCountWatch)) && parseInt(accompanyingChildrenCountWatch) > 0 && (
                    Array.from({ length: parseInt(accompanyingChildrenCountWatch) === 7 ? 6 : parseInt(accompanyingChildrenCountWatch) }).map((_, childIndex: number) => (
                      <div className="bg-white rounded-xl p-5 mt-5" key={childIndex}>
                        <p className="text-gray-900 text-lg mb-3 font-semibold">Child {childIndex + 1}</p>
                        <div className="flex gap-x-4">
                          <div className="w-1/6">
                            <label>Age</label>
                            <SelectDropdown
                              options={ageOfChildren}
                              name={`assessments.0.accompanyingChildrenDetails.${childIndex}.age`}
                              placeholder="Choose an option"
                              value={watch(`assessments.0.accompanyingChildrenDetails.${childIndex}.age`)}
                              register={register}
                              setValue={setValue}
                            />
                            {errors?.assessments?.[0]?.accompanyingChildrenDetails?.[childIndex]?.age?.type === "required" && (
                              <label className="text-error">
                                This field is required.
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </li>
            {/* history of violence */}
            {(mode === "legacy" || mode === "survivor") && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base"> History of violence, aggression or self-harming behaviors</div>
                <div className="flex flex-wrap gap-x-6 lg:gap-0">
                  {historyOfViolence.map((item: any, index: number) => (
                    <div className="mt-3 lg:w-1/3" key={index}>
                      <Checkbox
                        key={index}
                        label={item.label}
                        value={item.value}
                        checkboxTextClass="2xl:text-base"
                        selectedValue={watch("assessments.0.history")}
                        {...register("assessments.0.history", { required: true })}
                      />
                    </div>
                  ))}
                </div>
                {errors?.assessments?.[0]?.history?.type === "required" && (
                  <label className="text-error">
                    This field is required.
                  </label>
                )}
              </li>
            )}
            {/* mental health  */}
            {(mode === "legacy" || mode === "survivor") && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base"> Current mental health</div>
                <div className="flex flex-wrap gap-x-5">
                  {currentMentalHealth.map((item: any, index: number) => (
                    <div className="mt-3" key={index}>
                      <RadioButton
                        label={item.label}
                        value={item.value}
                        radioTextClass="2xl:text-base"
                        selectedValue={selectedValue}
                        radioClass="lg:mb-3 sm:text-gray-600"
                        {...register(`assessments.0.mentalHealth`, { required: true })}
                      />
                    </div>
                  ))}
                </div>
                {errors?.assessments?.[0]?.mentalHealth?.type === "required" && (
                  <label className="text-error">
                    This field is required.
                  </label>
                )}
              </li>
            )}
            {/* current substance use */}
            {(mode === "legacy" || mode === "survivor") && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base"> Current substance use</div>
                <div className="flex flex-wrap gap-x-5">
                  {currentSubstanceUse.map((item: any, index: number) => (
                    <div className="mt-3" key={index}>
                      <RadioButton
                        label={item.label}
                        value={item.value}
                        selectedValue={selectedValue}
                        radioTextClass="2xl:text-base"
                        radioClass="lg:mb-3 sm:text-gray-600"
                        {...register(`assessments.0.currentSubstanceUse`, { required: true })}
                      />
                    </div>
                  ))}
                </div>
                {errors?.assessments?.[0]?.currentSubstanceUse?.type === "required" && (
                  <label className="text-error">
                    This field is required.
                  </label>
                )}
              </li>
            )}
            {/* languages spoken */}
            {(mode !== 'rhy') && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base">
                  {" "}
                  Language(s) Spoken
                </div>
                <div className="flex flex-wrap sm:space-x-6">
                  <div className="w-full sm:w-1/3 lg:w-1/4 mt-3">
                    <InputBox
                      type="text"
                      placeholder="Primary Language"
                      {...register("assessments.0.primaryLanguage", {
                        maxLength: 150,
                        required: true,
                      })}
                    />
                    {errors?.assessments?.[0]?.primaryLanguage?.type ===
                      "required" && (
                      <label className="text-error">
                        This field is required.
                      </label>
                    )}
                  </div>
                  <div className="w-full sm:w-1/3 lg:w-1/4 mt-3">
                    <InputBox
                      type="text"
                      placeholder="Secondary Language"
                      {...register("assessments.0.secondaryLanguage", {
                        maxLength: 150,
                      })}
                    />
                  </div>
                </div>
              </li>
            )}
            {/* Language(s) Spoken */}
            {mode === "rhy" && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base">
                  Primary language spoken
                </div>
                <div className="flex flex-wrap gap-x-5">
                  {languageSpoken.map((item: any, index: number) => (
                    <div className="mt-3" key={index}>
                      <RadioButton
                        label={item.label}
                        value={item.value}
                        selectedValue={
                          watch("assessments.0.languageSpoken") ?? ""
                        } 
                        {...register("assessments.0.languageSpoken", {
                          required: true,
                        })}
                      />
                    </div>
                  ))}
                </div>
                {errors?.assessments?.[0]?.languageSpoken && (
                  <label className="text-error">This field is required.</label>
                )}
                {watch("assessments.0.languageSpoken") === "Other" && (
                  <div className="mt-1 w-72">
                    <InputBox
                      type="text"
                      placeholder="Other"
                      {...register("assessments.0.languageSpokenSpecial", {
                        maxLength: 150,
                        required:
                        watch("assessments.0.languageSpoken") === "Other",
                      })}
                    />
                  </div>
                )}
              </li>
            )}
            {/* English proficiency */}
            <li className="mb-8">
              <div className="text-gray-900 font-medium text-base"> English proficiency</div>
              <div className="flex flex-wrap gap-x-5">
                {englishProficiency.map((item: any, index: number) => (
                  <div className="mt-3" key={index}>
                    <RadioButton
                      label={item.label}
                      value={item.value}
                      radioTextClass="2xl:text-base"
                      selectedValue={selectedValue}
                      radioClass="lg:mb-3 sm:text-gray-600"
                      {...register(`assessments.0.englishProficiency`, { required: true })}
                    />
                  </div>
                ))}
              </div>
              {errors?.assessments?.[0]?.englishProficiency?.type === "required" && (
                <label className="text-error">
                  This field is required.
                </label>
              )}
            </li>
            {/* trafficking situation */}
            {(mode === "legacy" || mode === "survivor") && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base"> Type of trafficking situation or violence experienced</div>
                <div className="flex flex-wrap gap-x-4 lg:gap-0 ">
                  {traffickingSituation.map((item: any, index: number) => (
                    <div className="lg:w-1/5 mt-3" key={index}>
                      <Checkbox
                        key={index}
                        label={item.label}
                        value={item.value}
                        checkboxTextClass="2xl:text-base"
                        selectedValue={watch("assessments.0.traffickingSituation")}
                        {...register("assessments.0.traffickingSituation", { required: true })}
                      />
                    </div>
                  ))}
                  {mode === "legacy" && (
                    <Checkbox
                          label="Other"
                          value="Other"
                          key={"other123"}
                          checkboxTextClass="2xl:text-base"
                          selectedValue={watch("assessments.0.traffickingSituation")}
                          {...register("assessments.0.traffickingSituation", { required: true })}
                    />
                  )}
                </div>
                {errors?.assessments?.[0]?.traffickingSituation?.type === "required" && (
                  <label className="text-error">
                    This field is required.
                  </label>
                )}
                {mode === "legacy" && watch("assessments.0.traffickingSituation")?.includes("Other") && (
                  <div className="mt-1 w-72">
                    <InputBox
                      type="text"
                      placeholder="Please add other"
                      {...register("assessments.0.traffickingSituationSpecial", {
                        maxLength: 150,
                        required: watch("assessments.0.traffickingSituation")?.includes("Other"),
                        disabled: !watch("assessments.0.traffickingSituation")?.includes("Other"),
                      })}
                    />
                    {errors?.assessments?.[0]?.traffickingSituationSpecial?.type === "required" && (
                      <label className="text-error">
                        This field is required.
                      </label>
                    )}
                  </div>
                )}
              </li>
            )}
            {/* physical disability */}
            {(mode !== "rhy") && (
            <li className="mb-8">
              <div className="text-gray-900 font-medium text-base"> Physical Disability or mobility concerns requiring special consideration for the </div>
              <div className="flex flex-wrap gap-x-4 lg:gap-0">
                {physicalDisability.map((item: any, index: number) => (
                  <div className="lg:w-1/3 mt-3" key={index}>
                    <Checkbox
                      key={index}
                      label={item.label}
                      value={item.value}
                      checkboxTextClass="2xl:text-base"
                      selectedValue={watch("assessments.0.physicalDisability")}
                      {...register("assessments.0.physicalDisability", { required: true })}
                    />
                  </div>
                ))}
                {watch(`assessments.0.physicalDisability`).includes(physicalDisability[physicalDisability.length - 1].value) && (
                  <div className="mt-1 w-64">
                    <InputBox
                      type="text"
                      placeholder="Please add other"
                      {...register("assessments.0.physicalDisabilitySpecial", {
                        maxLength: 150,
                        required: watch(`assessments.0.physicalDisability`).includes(physicalDisability[physicalDisability.length - 1].value),
                        disabled: !watch(`assessments.0.physicalDisability`).includes(physicalDisability[physicalDisability.length - 1].value),
                      })}
                    />
                    {errors?.assessments?.[0]?.physicalDisabilitySpecial?.type === "required" && (
                      <label className="text-error">
                        This field is required.
                      </label>
                    )}
                  </div>
                )}
              </div>
              {errors?.assessments?.[0]?.physicalDisability?.type === "required" && (
                <label className="text-error">
                  This field is required.
                </label>
              )}
            </li>
            )}
            {/* Additional Information */}
            {mode !== "nyc" && (
              <li className="mb-8">
                <div className="text-gray-900 font-medium text-base">
                  What other information about your client and their situation is helpful in finding appropriate shelter for them? (Optional)
                </div>
                <div className="mt-3">
                  <InputBox
                    type="text"
                    placeholder=""
                    maxLength={120}
                    {...register("assessments.0.additionalInformation", {
                      maxLength: 120,
                    })}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Do not share any personally identifiable information about your client.
                    </p>
                    <p className="text-xs text-gray-500">
                      {additionalInformationWatch.length}/120
                    </p>
                  </div>
                </div>
              </li>
            )}
          </ol>
        )}
        <div className="flex justify-center gap-4 mt-10">
          <div>
            <button
              type="button"
              className="btn btn-outline-gray w-full h-full"
              onClick={() => {
                if (location.key !== "default") {
                  navigate(-1);
                } else {
                  navigate("/bedfinder", { replace: true });
                }
              }}
            >
              Go Back
            </button>
          </div>
          <button className="btn btn-primary">
            Send Request
          </button>
        </div>
      </form>
    </div>
  )
}

export default NewRequest
